import { useTranslation } from '@exam-room/translations';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from '../snackbar';

export const NetworkContext = createContext<{ isOnline: boolean }>({ isOnline: true });

interface NetworkProviderProps {
  children: React.ReactNode;
}

interface NetworkListener {
  onOnline?: () => void;
  onOffline?: () => void;
}

const listeners = new Set<NetworkListener>();

export const registerNetworkListener = (listener: NetworkListener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Check interval when offline
const OFFLINE_CHECK_INTERVAL = 5000; // 5 seconds
const CHECK_TIMEOUT = 5000; // 5 seconds

/**
 * Checks actual internet connectivity by making a request to a reliable endpoint.
 * Uses multiple fallback URLs to improve reliability.
 */
async function checkInternetConnectivity(): Promise<boolean> {
  // If browser reports offline, no need to check further
  if (!navigator.onLine) {
    return false;
  }

  // Try to fetch a small resource to verify actual connectivity
  const endpoints = ['https://www.google.com/generate_204'];

  for (const endpoint of endpoints) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CHECK_TIMEOUT); // 5 second timeout

      await fetch(endpoint, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      return true;
    } catch {
      continue;
    }
  }

  return false;
}

export function NetworkProvider({ children }: NetworkProviderProps) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isOnlineRef = useRef(navigator.onLine);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const clearCheckInterval = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  const setOnline = useCallback(() => {
    if (!isOnlineRef.current) {
      isOnlineRef.current = true;
      setIsOnline(true);
      clearCheckInterval();
      showSnackbar({
        message: t('examRoom.networkConnectedWarning'),
        variant: 'success',
      });
      for (const listener of listeners) {
        listener.onOnline?.();
      }
    }
  }, [clearCheckInterval, showSnackbar, t]);

  const setOffline = useCallback(() => {
    if (isOnlineRef.current) {
      isOnlineRef.current = false;
      setIsOnline(false);
      for (const listener of listeners) {
        listener.onOffline?.();
      }
    }
  }, []);

  const startOfflinePolling = useCallback(() => {
    clearCheckInterval();

    checkIntervalRef.current = setInterval(async () => {
      const online = await checkInternetConnectivity();
      if (online) {
        setOnline();
      }
    }, OFFLINE_CHECK_INTERVAL);
  }, [clearCheckInterval, setOnline]);

  useEffect(() => {
    const handleOnline = async () => {
      // Network connected -> verify actual internet connectivity
      const online = await checkInternetConnectivity();
      if (online) {
        setOnline();
      } else {
        // Network connected but no internet -> start polling
        setOffline();
        startOfflinePolling();
      }
    };

    const handleOffline = () => {
      setOffline();
      startOfflinePolling();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearCheckInterval();
    };
  }, [setOnline, setOffline, startOfflinePolling, clearCheckInterval]);

  return <NetworkContext.Provider value={{ isOnline }}>{children}</NetworkContext.Provider>;
}
